import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { 
  ChefHat, 
  ArrowLeft,
  Save,
  Plus,
  Trash2,
  GripVertical,
  Filter,
  Package,
  ArrowUpDown,
  Calendar,
  Scale,
  Pencil
} from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";

export const KitchenSettingsPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [handoverItems, setHandoverItems] = useState([]);
  const [filters, setFilters] = useState([]);
  const [availableColumns, setAvailableColumns] = useState([]);
  const [sortColumn, setSortColumn] = useState('Plán. datum od');
  const [sortDirection, setSortDirection] = useState('desc');
  
  // Tare items state
  const [tareItems, setTareItems] = useState([]);
  const [tareLoading, setTareLoading] = useState(false);
  const [tareDialogOpen, setTareDialogOpen] = useState(false);
  const [editingTareItem, setEditingTareItem] = useState(null);
  const [tareForm, setTareForm] = useState({ name: '', weight: '' });

  useEffect(() => {
    fetchSettings();
    fetchColumns();
    fetchTareItems();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const response = await api.get('/kitchen/settings');
      setHandoverItems(response.data.handover_items || []);
      setFilters(response.data.filters || []);
      setSortColumn(response.data.sort_column || 'Plán. datum od');
      setSortDirection(response.data.sort_direction || 'desc');
    } catch (error) {
      console.error('Error fetching settings:', error);
      toast.error('Chyba při načítání nastavení');
    } finally {
      setLoading(false);
    }
  };

  const fetchColumns = async () => {
    try {
      const response = await api.get('/production/columns');
      setAvailableColumns(response.data.columns || []);
    } catch (error) {
      console.error('Error fetching columns:', error);
    }
  };
  
  const fetchTareItems = async () => {
    try {
      const response = await api.get('/kitchen/tare-items');
      setTareItems(response.data || []);
    } catch (error) {
      console.error('Error fetching tare items:', error);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await api.put('/kitchen/settings', {
        handover_items: handoverItems,
        filters,
        sort_column: sortColumn,
        sort_direction: sortDirection
      });
      toast.success('Nastavení uloženo');
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error('Chyba při ukládání');
    } finally {
      setSaving(false);
    }
  };

  const addHandoverItem = () => {
    setHandoverItems([
      ...handoverItems,
      {
        id: `item_${Date.now()}`,
        name: '',
        unit: 'kg',
        order: handoverItems.length + 1
      }
    ]);
  };

  const updateHandoverItem = (id, field, value) => {
    setHandoverItems(items => 
      items.map(item => 
        item.id === id ? { ...item, [field]: value } : item
      )
    );
  };

  const removeHandoverItem = (id) => {
    setHandoverItems(items => items.filter(item => item.id !== id));
  };

  const addFilter = () => {
    setFilters([
      ...filters,
      { column: '', operator: 'equals', value: '', value_type: 'text' }
    ]);
  };

  const updateFilter = (index, fieldOrUpdates, value) => {
    // Support both: updateFilter(index, 'field', value) and updateFilter(index, { field: value })
    if (typeof fieldOrUpdates === 'string') {
      // Legacy call: updateFilter(index, field, value) 
      setFilters(prevFilters => prevFilters.map((f, i) => 
        i === index ? { ...f, [fieldOrUpdates]: value } : f
      ));
    } else {
      // New call: updateFilter(index, { field1: value1, field2: value2 })
      setFilters(prevFilters => prevFilters.map((f, i) => 
        i === index ? { ...f, ...fieldOrUpdates } : f
      ));
    }
  };

  const removeFilter = (index) => {
    setFilters(filters.filter((_, i) => i !== index));
  };

  // Tare items functions
  const openTareDialog = (item = null) => {
    if (item) {
      setEditingTareItem(item);
      setTareForm({ name: item.name, weight: item.weight.toString() });
    } else {
      setEditingTareItem(null);
      setTareForm({ name: '', weight: '' });
    }
    setTareDialogOpen(true);
  };

  const saveTareItem = async () => {
    if (!tareForm.name.trim()) {
      toast.error('Název je povinný');
      return;
    }
    if (!tareForm.weight || parseFloat(tareForm.weight) <= 0) {
      toast.error('Hmotnost musí být větší než 0');
      return;
    }

    try {
      setTareLoading(true);
      if (editingTareItem) {
        await api.put(`/kitchen/tare-items/${editingTareItem.id}`, {
          name: tareForm.name.trim(),
          weight: parseFloat(tareForm.weight)
        });
        toast.success('Položka aktualizována');
      } else {
        await api.post('/kitchen/tare-items', {
          name: tareForm.name.trim(),
          weight: parseFloat(tareForm.weight)
        });
        toast.success('Položka přidána');
      }
      await fetchTareItems();
      setTareDialogOpen(false);
    } catch (error) {
      console.error('Error saving tare item:', error);
      toast.error('Chyba při ukládání');
    } finally {
      setTareLoading(false);
    }
  };

  const deleteTareItem = async (id) => {
    if (!window.confirm('Opravdu chcete smazat tuto položku?')) return;
    
    try {
      await api.delete(`/kitchen/tare-items/${id}`);
      toast.success('Položka smazána');
      await fetchTareItems();
    } catch (error) {
      console.error('Error deleting tare item:', error);
      toast.error('Chyba při mazání');
    }
  };

  // Detect if column is a date column
  const isDateColumn = (column) => {
    const dateColumns = ['Plán. datum od', 'Datum potvrzení', 'Vytvořeno', 'EX_BestBefore'];
    return dateColumns.includes(column) || column?.toLowerCase().includes('datum');
  };

  // Get operators based on value type
  const getOperators = (valueType) => {
    if (valueType === 'date') {
      return [
        { value: 'equals', label: 'rovná se' },
        { value: 'less_than', label: 'menší než' },
        { value: 'less_or_equal', label: 'menší nebo rovno' },
        { value: 'greater_than', label: 'větší než' },
        { value: 'greater_or_equal', label: 'větší nebo rovno' },
        { value: 'today', label: 'dnes' },
        { value: 'today_or_before', label: '≤ dnes' },
        { value: 'yesterday', label: 'včera' },
        { value: 'this_week', label: 'tento týden' },
        { value: 'next_days', label: 'příštích X dní' }
      ];
    }
    return [
      { value: 'equals', label: 'rovná se' },
      { value: 'not_equals', label: 'nerovná se' },
      { value: 'contains', label: 'obsahuje' },
      { value: 'not_contains', label: 'neobsahuje' },
      { value: 'starts_with', label: 'začíná na' },
      { value: 'ends_with', label: 'končí na' }
    ];
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex justify-center items-center min-h-[60vh]">
          <div className="w-8 h-8 border-4 border-orange-500 border-t-transparent rounded-full animate-spin"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-6 max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <Button 
            variant="ghost" 
            size="icon"
            onClick={() => navigate('/admin')}
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex-1">
            <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
              <ChefHat className="w-7 h-7 text-orange-600" />
              Nastavení Kuchyně
            </h1>
            <p className="text-slate-500 mt-1">Položky předávky, filtry a řazení</p>
          </div>
          <Button onClick={handleSave} disabled={saving} className="bg-orange-600 hover:bg-orange-700">
            <Save className="w-4 h-4 mr-2" />
            {saving ? 'Ukládám...' : 'Uložit'}
          </Button>
        </div>

        {/* Sorting */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <ArrowUpDown className="w-5 h-5" />
              Implicitní řazení
            </CardTitle>
            <CardDescription>
              Nastavte podle kterého sloupce a jak se mají záznamy řadit
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1">
                <label className="block text-sm font-medium text-slate-700 mb-2">Řadit podle</label>
                <Select value={sortColumn} onValueChange={setSortColumn}>
                  <SelectTrigger>
                    <SelectValue placeholder="Vyberte sloupec" />
                  </SelectTrigger>
                  <SelectContent className="max-h-60 overflow-y-auto">
                    {availableColumns.map((col) => (
                      <SelectItem key={col} value={col} className="cursor-pointer">{col}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="w-48">
                <label className="block text-sm font-medium text-slate-700 mb-2">Směr</label>
                <Select value={sortDirection} onValueChange={setSortDirection}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="asc">Vzestupně (A→Z, 1→9)</SelectItem>
                    <SelectItem value="desc">Sestupně (Z→A, 9→1)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Handover Items */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Package className="w-5 h-5" />
              Položky předávky
            </CardTitle>
            <CardDescription>
              Definujte co má kuchař vyplnit při předávce na balírnu (např. omáčka, maso, příloha)
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {handoverItems.map((item, index) => (
                <div key={item.id} className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                  <GripVertical className="w-4 h-4 text-slate-400 cursor-move" />
                  <span className="text-sm text-slate-500 w-6">{index + 1}.</span>
                  <Input
                    placeholder="Název položky (např. Omáčka)"
                    value={item.name}
                    onChange={(e) => updateHandoverItem(item.id, 'name', e.target.value)}
                    className="flex-1"
                  />
                  <Select
                    value={item.unit}
                    onValueChange={(value) => updateHandoverItem(item.id, 'unit', value)}
                  >
                    <SelectTrigger className="w-24">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="kg">kg</SelectItem>
                      <SelectItem value="l">l</SelectItem>
                      <SelectItem value="ks">ks</SelectItem>
                      <SelectItem value="g">g</SelectItem>
                      <SelectItem value="ml">ml</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => removeHandoverItem(item.id)}
                    className="text-red-500 hover:text-red-700 hover:bg-red-50"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>
            <Button
              variant="outline"
              onClick={addHandoverItem}
              className="mt-4 w-full"
            >
              <Plus className="w-4 h-4 mr-2" />
              Přidat položku
            </Button>
          </CardContent>
        </Card>

        {/* Tare Items (Gastro nádoby, vozíky) */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Scale className="w-5 h-5" />
              Hmotnosti nádob a vozíků (Tára)
            </CardTitle>
            <CardDescription>
              Nastavte hmotnosti gastro nádob a vozíků pro odečet od celkové váhy při předávce
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {tareItems.map((item) => (
                <div key={item.id} className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                  <div className="flex-1">
                    <span className="font-medium">{item.name}</span>
                  </div>
                  <div className="text-right min-w-[80px]">
                    <span className="font-semibold text-orange-600">{item.weight} kg</span>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => openTareDialog(item)}
                    className="text-slate-500 hover:text-slate-700"
                  >
                    <Pencil className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => deleteTareItem(item.id)}
                    className="text-red-500 hover:text-red-700 hover:bg-red-50"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))}
              {tareItems.length === 0 && (
                <p className="text-sm text-slate-500 text-center py-4">
                  Zatím nejsou definovány žádné položky táry
                </p>
              )}
            </div>
            <Button
              variant="outline"
              onClick={() => openTareDialog()}
              className="mt-4 w-full"
            >
              <Plus className="w-4 h-4 mr-2" />
              Přidat nádobu/vozík
            </Button>
          </CardContent>
        </Card>

        {/* Filters */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Filter className="w-5 h-5" />
              Filtry výrobních záznamů
            </CardTitle>
            <CardDescription>
              Nastavte filtry pro zobrazení pouze relevantních výrobních záznamů v Kuchyni
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {filters.map((filter, index) => {
                const valueType = isDateColumn(filter.column) ? 'date' : 'text';
                const operators = getOperators(valueType);
                const needsValue = !['today', 'today_or_before', 'yesterday', 'this_week'].includes(filter.operator);
                
                return (
                  <div key={index} className="p-4 bg-slate-50 rounded-lg space-y-3">
                    <div className="flex items-center gap-3">
                      <div className="flex-1">
                        <label className="block text-xs text-slate-500 mb-1">Sloupec</label>
                        <Select
                          value={filter.column}
                          onValueChange={(value) => {
                            // Update column and value_type together to avoid race condition
                            const newValueType = isDateColumn(value) ? 'date' : 'text';
                            updateFilter(index, { 
                              column: value, 
                              value_type: newValueType 
                            });
                          }}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Vyberte sloupec" />
                          </SelectTrigger>
                          <SelectContent className="max-h-60 overflow-y-auto">
                            {availableColumns.map((col) => (
                              <SelectItem key={col} value={col} className="cursor-pointer">
                                {col}
                                {isDateColumn(col) && <Calendar className="w-3 h-3 ml-2 inline text-blue-500" />}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      
                      <div className="w-48">
                        <label className="block text-xs text-slate-500 mb-1">Operátor</label>
                        <Select
                          value={filter.operator}
                          onValueChange={(value) => updateFilter(index, 'operator', value)}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Vyberte operátor" />
                          </SelectTrigger>
                          <SelectContent className="max-h-60 overflow-y-auto">
                            {operators.map((op) => (
                              <SelectItem key={op.value} value={op.value} className="cursor-pointer">{op.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => removeFilter(index)}
                        className="text-red-500 hover:text-red-700 hover:bg-red-50 mt-5"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                    
                    {needsValue && (
                      <div>
                        <label className="block text-xs text-slate-500 mb-1">
                          {filter.operator === 'next_days' ? 'Počet dní' : 'Hodnota'}
                        </label>
                        {valueType === 'date' && filter.operator !== 'next_days' ? (
                          <Input
                            type="date"
                            value={filter.value}
                            onChange={(e) => updateFilter(index, 'value', e.target.value)}
                            className="w-full"
                          />
                        ) : filter.operator === 'next_days' ? (
                          <Input
                            type="number"
                            placeholder="např. 7"
                            value={filter.value}
                            onChange={(e) => updateFilter(index, 'value', e.target.value)}
                            className="w-32"
                          />
                        ) : (
                          <Input
                            placeholder="Hodnota"
                            value={filter.value}
                            onChange={(e) => updateFilter(index, 'value', e.target.value)}
                            className="w-full"
                          />
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            <Button
              variant="outline"
              onClick={addFilter}
              className="mt-4 w-full"
            >
              <Plus className="w-4 h-4 mr-2" />
              Přidat filtr
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Tare Item Dialog */}
      <Dialog open={tareDialogOpen} onOpenChange={setTareDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingTareItem ? 'Upravit položku' : 'Nová položka táry'}
            </DialogTitle>
            <DialogDescription>
              Zadejte název a hmotnost nádoby nebo vozíku
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Název <span className="text-red-500">*</span>
              </label>
              <Input
                placeholder="např. Gastro nádoba GN 1/1"
                value={tareForm.name}
                onChange={(e) => setTareForm({ ...tareForm, name: e.target.value })}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Hmotnost (kg) <span className="text-red-500">*</span>
              </label>
              <Input
                type="number"
                step="0.1"
                min="0"
                placeholder="např. 2.5"
                value={tareForm.weight}
                onChange={(e) => setTareForm({ ...tareForm, weight: e.target.value })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTareDialogOpen(false)}>
              Zrušit
            </Button>
            <Button onClick={saveTareItem} disabled={tareLoading} className="bg-orange-600 hover:bg-orange-700">
              {tareLoading ? 'Ukládám...' : (editingTareItem ? 'Uložit' : 'Přidat')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default KitchenSettingsPage;
